<script setup>
import CreateBannerAction from "@frontJs/components/actions/projectManagement/sem/CreateBannerAction.vue";
import BannerActionsCell from "@frontJs/components/tables/cells/projectManagement/sem/BannerActionsCell.vue";
import { PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import PillCell from "@js/components/tables/cells/PillCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { tenant, all } = defineProps({
  tenant: {
    type: Object,
    required: false,
  },
  all: {
    type: Boolean,
    required: false,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
  globalData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "tenant.name",
      header: () => t("label.customer"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.tenant.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "id",
      header: () => t("label.id"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.id,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "websiteVariant",
      header: () => t("label.websiteVariant"),
      cell: (props) =>
        h(PillCell, {
          pillValue: props.row.original.websiteVariant.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "subject",
      header: () => t("label.subject"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.subject,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "sentAt",
      header: () => t("label.sentAt"),
      cell: (props) =>
        props.row.original.sentAt
          ? h(TextCellV2, {
              text: formatDate(props.row.original.sentAt),
            })
          : h(CrossIconCell),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(BannerActionsCell, {
          tenant: tenant,
          row: innerProps.row,
        }),
    }),
  ],
  url: route("front.projectManagement.sem.postBanners", {
    tenant: tenant ? tenant.id : null,
    all,
  }),
  columnPinning: {
    right: ["actions"],
  },
  columnVisibility: {
    "tenant.name": !tenant,
    id: !!tenant,
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :filters="filters"
    :available-filters="availableFilters"
    :set-filters="setFilters"
    :set-global-filter="setGlobalFilter"
  >
    <template #actions>
      <ActionButton
        v-if="tenant"
        size="small"
        :href="
          route('front.projectManagement.sem.banner', {
            tenant: tenant.id,
          })
        "
        :left-icon="PlusIcon"
      >
        {{ t("button.add") }}
      </ActionButton>

      <CreateBannerAction v-else :tenants="globalData.tenants">
        <template #button="{ setIsOpen }">
          <ActionButton
            size="small"
            :left-icon="PlusIcon"
            :callback="() => setIsOpen(true)"
          >
            {{ t("button.add") }}
          </ActionButton>
        </template>
      </CreateBannerAction>
    </template>
  </DataTableLayout>
</template>
